// lib/slides/counterProposal.ts
//
// The "level of ambition" counter-proposal.
//
// A review that only lists faults reads as hostile and gives the tenant nothing
// to aim at. The RDM team's answer, described in the review doctrine, is to
// append a re-rendered "Concept Studio proposal" of the storefront and annotate
// it with GREEN bubbles saying what good looks like ("3D illuminated lettering
// on the glass", "LED perimeter"). Until now the Reviewer could not produce one:
// the doctrine literally told it "You cannot generate imagery from this tool -
// ask the user to attach it".
//
// This module owns the two pure pieces of making one: turning a region of the
// page into the crop the image model edits, and turning the review's own
// findings into the instruction it edits by. Both are pure so they can be
// tested without a model call, which matters because the model call is the
// expensive half.
//
// The crop is deliberately taken from the ORIGINAL page snapshot rather than a
// re-render: the proposal has to be recognisably the tenant's own unit, or it
// is a mood board rather than a counter-proposal.

/** A rectangle in PAGE POINTS, top-left origin - the space overlay frames use. */
export interface PageRegion {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** The same rectangle in snapshot PIXELS, which is what sharp crops in. */
export interface PixelRegion {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * Convert a page-point region to a pixel region inside a snapshot of known
 * width, clamped to the image.
 *
 * The snapshot is a raster of the whole page at some arbitrary resolution, so
 * the only reliable scale is pixels-per-point derived from the width - the same
 * derivation anchorLocator uses (`pxPerPt = pxW / pageW`). Deriving it from
 * height instead would drift on any page whose raster was letterboxed.
 *
 * Clamping matters: the agent names a region from what it can see, and a
 * bubble-adjacent region routinely runs a few points off the sheet. Sharp
 * throws on an out-of-bounds extract, and a throw here would lose the whole
 * proposal over a rounding error.
 */
export function pageRegionToPixels(
  region: PageRegion,
  pageW: number,
  pixelW: number,
  pixelH: number
): PixelRegion | null {
  if (!(pageW > 0) || !(pixelW > 0) || !(pixelH > 0)) return null;
  const pxPerPt = pixelW / pageW;

  const rawLeft = Math.round(region.x * pxPerPt);
  const rawTop = Math.round(region.y * pxPerPt);
  const rawW = Math.round(region.w * pxPerPt);
  const rawH = Math.round(region.h * pxPerPt);

  const left = Math.max(0, Math.min(rawLeft, pixelW - 1));
  const top = Math.max(0, Math.min(rawTop, pixelH - 1));
  const width = Math.max(1, Math.min(rawW, pixelW - left));
  const height = Math.max(1, Math.min(rawH, pixelH - top));

  // A sliver is not a storefront. Below this the model is being handed noise and
  // will invent a building; refusing is better than returning a fiction.
  if (width < MIN_CROP_PX || height < MIN_CROP_PX) return null;

  return { left, top, width, height };
}

/** Under this many pixels on a side the crop carries no usable detail. */
export const MIN_CROP_PX = 64;

/**
 * Fit an image of known intrinsic size inside a box WITHOUT distorting it,
 * centred on both axes.
 *
 * Slides image elements render with `objectFit: 'fill'` on the canvas
 * (SlidesCanvas.tsx) and with pdf-lib's drawImage at the frame's exact width
 * and height on export - both of which stretch the picture to whatever the
 * frame says. So the frame IS the aspect ratio, and a frame computed from page
 * fractions alone silently reshapes the storefront.
 *
 * Measured on the first real run: the pair was placed in a 1482.94 x 1430.35
 * box (ratio 1.04) while both images were ratio 1.775, stretching the shopfront
 * 71% vertically in the editor and in the exported PDF. A counter-proposal that
 * misreports the proportions of the tenant's own unit argues against itself.
 */
export function fitInBox(
  imageW: number,
  imageH: number,
  box: PageRegion
): PageRegion {
  // An unmeasurable image (a 0, a NaN, an asset that never loaded) keeps the
  // full box: no aspect information means no correction to make, and the box is
  // the caller's own layout.
  if (!(imageW > 0) || !(imageH > 0) || !(box.w > 0) || !(box.h > 0)) return { ...box };
  const scale = Math.min(box.w / imageW, box.h / imageH);
  const w = imageW * scale;
  const h = imageH * scale;
  return { x: box.x + (box.w - w) / 2, y: box.y + (box.h - h) / 2, w, h };
}

/**
 * The CROQUIS pass: the reviewer's remarks drawn onto the tenant's own render.
 *
 * This is how the RDM team actually marks up a proposal - blue pen over the
 * render, an arrow to the thing, two words about it - and it is what the tenant
 * recognises as a review. A list of bubbles beside a picture is our format; this
 * is theirs.
 *
 * It also feeds the second pass. Handing the model an image with the corrections
 * DRAWN ON IT carries something a prose instruction cannot: position. "Reduce the
 * secondary signs on the dividers" leaves the model to find the dividers;
 * an arrow pointing at them does not.
 *
 * The hard part is legibility. This same model mangles words it redraws - a
 * shipped sheet once carried "A NIALEGONSONT EXPERIENCE" - and here the words are
 * the point, so the instruction is explicit about short phrases, real spelling and
 * the language of the remarks themselves. The croquis critic then reads every one
 * back before the page is placed.
 */
export function buildCroquisPrompt(changes: string[], drawingNotes: string[] = []): string {
  const asked = changes
    .map((c) => c.trim())
    .filter(Boolean)
    .map((c, i) => `${i + 1}. ${c.replace(/\s+/g, ' ')}`);

  return [
    'Annotate this architectural render the way a design reviewer marks up a drawing by hand.',
    '',
    'Draw ON TOP of the image, in BLUE freehand pen, as if someone had drawn on a print:',
    '- a short handwritten note for each remark below, in the SAME LANGUAGE the remark is written in',
    '- a freehand arrow or a loose circle from each note to the exact element it is about',
    '- nothing else: no boxes, no legend, no numbering, no title block, no watermark',
    '',
    'The remarks to write, one annotation each:',
    ...(asked.length > 0 ? asked : ['1. Bring the shopfront into line with the landlord design guidelines.']),
    '',
    'The render underneath must be COMPLETELY UNCHANGED. Do not correct, improve, relight,',
    're-render or clean anything in it. This pass only adds the pen marks; the corrections',
    'themselves happen later. If the render changes, the annotation is worthless as a record',
    'of what was asked.',
    '',
    'Handwriting rules, and they matter more than the drawing:',
    '- every word must be a REAL, correctly spelled word',
    '- write LARGE. Each note should be readable when the whole image is viewed at about a',
    '  quarter of its size, the way a reviewer reads a sheet across a desk. Big, confident pen',
    '  strokes, not neat small script.',
    '- SIX WORDS OR FEWER per note. "Dégager vitrages latéraux" is a note; a full sentence with',
    '  a subordinate clause is not. Cut words until it fits and stays large.',
    '- do not invent extra commentary beyond the remarks listed above',
    '- if a note will not fit large and legible, write fewer words. Never write smaller, and',
    '  never approximate a word you cannot form.',
    // The critic's corrections are instructions to YOU, not remarks for the
    // tenant. Folded into the remark list on a retry, the model dutifully wrote
    // them on the sheet: a live draft carried "Fix : Remplacer les notes abregees
    // par les six remarques completes" in blue pen, addressed to nobody.
    ...(drawingNotes.length > 0
      ? [
          '',
          'Corrections to HOW you draw this, from the previous attempt. These are instructions',
          'to you. They are NOT remarks and must NOT be written anywhere on the image:',
          ...drawingNotes.map((n) => `- ${n.trim().replace(/\s+/g, ' ')}`),
        ]
      : []),
  ].join('\n');
}

/**
 * The APPLIED pass: the corrected design, generated FROM the annotated croquis.
 *
 * The input is the croquis image, not the clean render, and that is the whole
 * point of doing it this way: the model is shown where each correction goes
 * rather than told, so it does not have to re-find the elements from prose. The
 * changes are still listed, because the pen marks say WHERE and the words say
 * WHAT.
 *
 * Its first job is to remove the very marks it is reading from. A proposal that
 * ships with the reviewer's blue pen still on it is not a proposal.
 */
export function buildAppliedFromCroquisPrompt(changes: string[], brandBrief?: string): string {
  const asked = changes
    .map((c) => c.trim())
    .filter(Boolean)
    .map((c) => `- ${c.replace(/\s+/g, ' ')}`);

  return [
    'This image is an architectural render with a reviewer\'s handwritten corrections drawn on it',
    'in blue pen. Produce the CORRECTED render.',
    '',
    'Do both of these:',
    '1. REMOVE every blue pen mark - the handwriting, the arrows, the circles. None of them may',
    '   survive into the result. The output is a clean photorealistic render with no annotation.',
    '2. APPLY what each mark asked for, at the exact place the arrow or circle points to.',
    '',
    'The annotations say WHERE. These are the same corrections in words:',
    ...(asked.length > 0 ? asked : ['- Bring the shopfront into line with the landlord design guidelines.']),
    '',
    'Apply EVERY correction EVERYWHERE it applies. When one names a KIND of thing - applied',
    'graphics, vinyl, secondary signs, screens, opaque panels - correct EVERY instance across the',
    'whole scene, not only the one the pen happened to touch. A correction applied in one place',
    'and not the others is a FAILED edit, not a partial success.',
    '',
    ...(brandBrief?.trim() ? [
      'Vetted brand inspiration (additional images are references, never the base design):',
      brandBrief.trim().slice(0, 6000),
      'Guidelines and project constraints have absolute priority over brand inspiration. References do not establish compliance.',
      'Transfer brand character only where compatible: never overwrite submitted geometry, viewpoint or material identity except for the explicit corrections above.',
      '',
    ] : []),
    'Keep unchanged: the unit position and width, the ceiling height, the neighbouring units and',
    'the context. This must read as the SAME space, improved - not a different one.',
    'Keep the source camera angle, storefront geometry and material identity except for the',
    'explicit corrections above. Produce a photorealistic improvement of the SAME design.',
    'Refine material textures at a realistic scale, with coherent lighting, shadows and reflections',
    'that reveal the materials and spatial depth. Improve flat lighting without hiding defects.',
    'Use restrained depth of field only where appropriate; keep the storefront, signage and',
    'compliance details sharp and readable. Never blur the design under review.',
    'These rendering directions are not handwritten remarks; do not write them on the image.',
    'Do NOT draw any text, labels, dimensions, arrows or annotations into the result.',
    // Left as a conditional ("remove it IF you cannot reproduce it"), this was
    // ignored run after run: the model always believes it can, and the plinth came
    // back reading "A MLLE@GENY EXPERIENCE", then "A MUL TIENSISON EXPERIENCE".
    // Small secondary lettering carries nothing in a design proposal, so it is now
    // a directive rather than a judgement call the model keeps getting wrong.
    'REMOVE all small secondary lettering: plinth captions, wall labels, product tags, strap',
    'lines, anything set in small type inside the space. Leave those surfaces blank. Do not',
    'attempt to redraw small text at all - every attempt so far has produced nonsense, and one',
    'garbled word makes the whole sheet unsendable.',
    'The main shopfront sign is the ONE exception: keep it only if you can reproduce its words',
    'exactly as they read in the source, letter for letter. Otherwise remove it too.',
  ].join('\n');
}
